import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { apiFetch } from "./sdk"

/**
 * Settings → Customer messages → Gmail (backend lib/gmail/settings.ts): the support mailbox read into
 * admin → Messages and, in mode "full", replies sent through it. Connected by signing in with Google
 * as the mailbox (default) or with a service-account key ("Other method"). No key, token or OAuth
 * client secret is ever sent back.
 */
export type GmailMode = "off" | "import" | "full"
export type GmailAuthMethod = "oauth" | "service_account"
export type GmailOAuthInfo = {
  client: { configured: boolean; source: "admin" | "env" | null; client_id: string | null; secret_hint: string | null; env_configured: boolean; admin_saved: boolean; updated_at: string | null; updated_by: string | null }
  redirect_uri: string
  scopes: string[]
  default_mailbox: string
  setup_steps: string[]
  last_result: { ok: boolean; message: string; at: string; by: string | null } | null
}
export type GmailStatus = {
  connected: boolean
  auth_method?: GmailAuthMethod
  oauth: GmailOAuthInfo
  oauth_client_id?: string | null
  oauth_scopes?: string[] | null
  /** Sign in with Google can't be used until an owner reconnects (plain reason). */
  needs_reconnect?: string | null
  needs_reconnect_at?: string | null
  readable?: boolean
  mailbox?: string
  display_name?: string
  client_email?: string | null
  client_id?: string | null
  project_id?: string | null
  key_hint?: string | null
  mode: GmailMode
  import_days?: number
  label_enabled?: boolean
  label_name?: string
  mark_read?: boolean
  import?: { started_at: string | null; done_at: string | null; threads: number; running: boolean }
  last_sync_at?: string | null
  last_sync_ok_at?: string | null
  last_error?: string | null
  last_resync_at?: string | null
  last_test?: { at: string; ok: boolean | null; message: string | null } | null
  connected_at?: string
  connected_by?: string | null
  counts?: { email_conversations?: number; not_customer?: number; emails?: number }
  setup_steps: string[]
  scopes: string[]
  can_manage: boolean
}
type Resp = { gmail: GmailStatus; test?: { ok: boolean; message: string } }
export type GmailOptions = Partial<{ mode: GmailMode; mailbox: string; display_name: string; import_days: number; label_enabled: boolean; label_name: string; mark_read: boolean }>

const KEY = ["settings", "gmail"]
const URL_ = "/admin/terify/settings/gmail"

export function useGmail(enabled = true) {
  return useQuery({ queryKey: KEY, enabled, queryFn: () => apiFetch<Resp>(URL_), refetchInterval: (q) => ((q.state.data as Resp | undefined)?.gmail.import?.running ? 10_000 : 60_000) })
}

function useGmailMutation<V, R = Resp>(fn: (v: V) => Promise<R>) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: (r: any) => {
      if (r?.gmail) qc.setQueryData(KEY, (old: Resp | undefined) => ({ ...old, gmail: r.gmail }))
      else qc.invalidateQueries({ queryKey: KEY })
      qc.invalidateQueries({ queryKey: ["inbox"] })
    },
  })
}

const post = <R,>(path: string, body: unknown, method = "POST") => apiFetch<R>(path, { method, body: JSON.stringify(body) })
export const useConnectGmail = () => useGmailMutation((b: { key_json: string; mailbox: string; display_name?: string }) => post<Resp>(URL_, b))
export const useDisconnectGmail = () => useGmailMutation(() => apiFetch<Resp & { revoked: boolean | null }>(URL_, { method: "DELETE" }))
/** "Sign in with Google as <mailbox>": the backend returns Google's sign-in URL; the browser goes there and comes back to Settings → Customer messages. */
export const useStartGmailOAuth = () => useMutation({ mutationFn: (b: { mailbox?: string; display_name?: string }) => post<{ url: string; mailbox: string; redirect_uri: string }>(`${URL_}/oauth/start`, b) })
export const useSaveGmailOAuthClient = () => useGmailMutation((b: { client_id: string; client_secret: string }) => post<Resp>(`${URL_}/oauth/client`, b))
export const useClearGmailOAuthClient = () => useGmailMutation(() => apiFetch<Resp>(`${URL_}/oauth/client`, { method: "DELETE" }))
export const useGmailOptions = () => useGmailMutation((b: GmailOptions) => post<Resp>(`${URL_}/options`, b))
export const useTestGmail = () => useGmailMutation(() => post<{ test: { ok: boolean; message: string } }>(`${URL_}/connection-test`, {}))
export const useSyncGmail = () =>
  useGmailMutation(() => post<{ result: { threads: number; added: number; created: number; errors: number; error?: string | null; busy?: boolean; skipped?: boolean; phase?: string; done?: boolean }; gmail: GmailStatus }>(`${URL_}/sync`, {}))

export const MODE_LABEL: Record<GmailMode, string> = { off: "Off", import: "Import only (read)", full: "Full (read and send)" }
